import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../contexts/AppContext';
import { ChevronLeftIcon, BellIcon, MoonIcon, GlobeIcon, ShieldIcon, HelpCircleIcon, InfoIcon, ChevronDownIcon } from 'lucide-react';
interface SettingsProps {
  onBack: () => void;
}
export function Settings({
  onBack
}: SettingsProps) {
  const {
    colors,
    theme,
    setTheme
  } = useTheme();
  const {
    lastSynced
  } = useApp();
  const [pushNotifications, setPushNotifications] = useState(true);
  const [criticalAlerts, setCriticalAlerts] = useState(true);
  const [offlineMode, setOfflineMode] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [language, setLanguage] = useState('English');
  const handleClearCache = () => {
    alert('Cache cleared successfully');
  };
  const formatLastSynced = () => {
    if (!lastSynced) return 'Never';
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastSynced.getTime()) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} mins ago`;
    const hours = Math.floor(diff / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };
  const ToggleSwitch = ({
    enabled,
    onChange
  }: {
    enabled: boolean;
    onChange: (value: boolean) => void;
  }) => <button onClick={() => onChange(!enabled)} className="relative w-12 h-6 rounded-full transition-colors" style={{
    backgroundColor: enabled ? colors.accent : colors.divider
  }} data-id="element-450">
      <div className="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform" style={{
      transform: enabled ? 'translateX(26px)' : 'translateX(4px)'
    }} data-id="element-451" />
    </button>;
  return <div className="min-h-screen -m-4" style={{
    backgroundColor: colors.background
  }} data-id="element-452">
      <div className="sticky top-0 z-10 p-4 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider
    }} data-id="element-453">
        <div className="flex items-center gap-4" data-id="element-454">
          <button onClick={onBack} className="p-2 -ml-2" style={{
          color: colors.textPrimary
        }} data-id="element-455">
            <ChevronLeftIcon size={24} data-id="element-456" />
          </button>
          <div data-id="element-457">
            <h1 className="text-xl font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-458">
              Settings
            </h1>
            <p className="text-sm" style={{
            color: colors.textSecondary
          }} data-id="element-459">
              Customize your experience
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4" data-id="element-460">
        {/* Notifications */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-461">
          <div className="flex items-center gap-3 mb-4" data-id="element-462">
            <BellIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-463" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-464">
              Notifications
            </h2>
          </div>
          <div className="space-y-4" data-id="element-465">
            <div className="flex items-center justify-between" data-id="element-466">
              <div data-id="element-467">
                <div className="font-medium" style={{
                color: colors.textPrimary
              }} data-id="element-468">
                  Push Notifications
                </div>
                <div className="text-sm" style={{
                color: colors.textSecondary
              }} data-id="element-469">
                  Receive alerts and updates
                </div>
              </div>
              <ToggleSwitch enabled={pushNotifications} onChange={setPushNotifications} data-id="element-470" />
            </div>
            <div className="flex items-center justify-between" data-id="element-471">
              <div data-id="element-472">
                <div className="font-medium" style={{
                color: colors.textPrimary
              }} data-id="element-473">
                  Critical Alerts
                </div>
                <div className="text-sm" style={{
                color: colors.textSecondary
              }} data-id="element-474">
                  High priority notifications
                </div>
              </div>
              <ToggleSwitch enabled={criticalAlerts} onChange={setCriticalAlerts} data-id="element-475" />
            </div>
          </div>
        </div>
        {/* Appearance */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-476">
          <div className="flex items-center gap-3 mb-4" data-id="element-477">
            <MoonIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-478" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-479">
              Appearance
            </h2>
          </div>
          <div className="flex items-center justify-between" data-id="element-480">
            <div data-id="element-481">
              <div className="font-medium" style={{
              color: colors.textPrimary
            }} data-id="element-482">
                Dark Mode
              </div>
              <div className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-483">
                Use dark theme
              </div>
            </div>
            <ToggleSwitch enabled={theme === 'dark'} onChange={value => setTheme(value ? 'dark' : 'light')} data-id="element-484" />
          </div>
        </div>
        {/* Data & Sync */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-485">
          <div className="flex items-center gap-3 mb-4" data-id="element-486">
            <GlobeIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-487" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-488">
              Data & Sync
            </h2>
          </div>
          <div className="space-y-4" data-id="element-489">
            <div className="flex items-center justify-between" data-id="element-490">
              <div data-id="element-491">
                <div className="font-medium" style={{
                color: colors.textPrimary
              }} data-id="element-492">
                  Offline Mode
                </div>
                <div className="text-sm" style={{
                color: colors.textSecondary
              }} data-id="element-493">
                  Work without internet
                </div>
              </div>
              <ToggleSwitch enabled={offlineMode} onChange={setOfflineMode} data-id="element-494" />
            </div>
            <div className="flex items-center justify-between" data-id="element-495">
              <div data-id="element-496">
                <div className="font-medium" style={{
                color: colors.textPrimary
              }} data-id="element-497">
                  Auto Sync
                </div>
                <div className="text-sm" style={{
                color: colors.textSecondary
              }} data-id="element-498">
                  Sync data automatically
                </div>
              </div>
              <ToggleSwitch enabled={autoSync} onChange={setAutoSync} data-id="element-499" />
            </div>
            <button onClick={handleClearCache} className="w-full p-4 rounded-full font-medium" style={{
            color: colors.accent,
            border: `2px solid ${colors.accent}`
          }} data-id="element-500">
              Clear Cache
            </button>
            <div className="text-center text-sm" style={{
            color: colors.textSecondary
          }} data-id="element-501">
              Last synced: {formatLastSynced()}
            </div>
          </div>
        </div>
        {/* Language */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-502">
          <div className="flex items-center gap-3 mb-4" data-id="element-503">
            <GlobeIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-504" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-505">
              Language
            </h2>
          </div>
          <div className="p-4 rounded-lg flex items-center justify-between" style={{
          backgroundColor: colors.background,
          border: `1px solid ${colors.divider}`
        }} data-id="element-506">
            <span style={{
            color: colors.textPrimary
          }} data-id="element-507">
              {language}
            </span>
            <ChevronDownIcon size={20} style={{
            color: colors.textSecondary
          }} data-id="element-508" />
          </div>
        </div>
        {/* Privacy & Security */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-509">
          <div className="flex items-center gap-3 mb-4" data-id="element-510">
            <ShieldIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-511" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-512">
              Privacy & Security
            </h2>
          </div>
          <div className="space-y-3" data-id="element-513">
            <button className="w-full p-4 rounded-full font-medium" style={{
            color: colors.accent,
            border: `2px solid ${colors.accent}`
          }} data-id="element-514">
              Privacy Policy
            </button>
            <button className="w-full p-4 rounded-full font-medium" style={{
            color: colors.accent,
            border: `2px solid ${colors.accent}`
          }} data-id="element-515">
              Terms of Service
            </button>
          </div>
        </div>
        {/* Support */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-516">
          <div className="flex items-center gap-3 mb-4" data-id="element-517">
            <HelpCircleIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-518" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-519">
              Support
            </h2>
          </div>
          <div className="space-y-3" data-id="element-520">
            <button className="w-full p-4 rounded-full font-medium" style={{
            color: colors.accent,
            border: `2px solid ${colors.accent}`
          }} data-id="element-521">
              Help Center
            </button>
            <button className="w-full p-4 rounded-full font-medium" style={{
            color: colors.accent,
            border: `2px solid ${colors.accent}`
          }} data-id="element-522">
              Contact Support
            </button>
          </div>
        </div>
        {/* About */}
        <div className="p-6 rounded-xl" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-523">
          <div className="flex items-center gap-3 mb-4" data-id="element-524">
            <InfoIcon size={24} style={{
            color: colors.textPrimary
          }} data-id="element-525" />
            <h2 className="text-lg font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-526">
              About
            </h2>
          </div>
          <div className="space-y-2" style={{
          color: colors.textSecondary
        }} data-id="element-527">
            <div data-id="element-528">Version 1.0.0</div>
            <div data-id="element-529">© 2024 Clinical Decision Support</div>
          </div>
        </div>
      </div>
    </div>;
}